import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Filter, LayoutGrid, Table2, Download, Search, Archive, Box, Printer, Users, Mail, X, RefreshCw, GitMerge, Calendar } from "lucide-react";
import {
  trackerListJobs, trackerGetMeta, trackerCreateJob, trackerUpdateJob, trackerDownloadCsv,
  trackerNotifyCustomer, trackerMergeJobs, trackerArchiveStats,
  STATUS_COLORS, PRIORITY_COLORS, QUEUE_COLORS, PRODUCT_TAG_COLORS,
  isJobLate,
} from "../../../lib/tracker";
import { CUSTOMER_TYPE_COLORS, customerSendStatusEmail } from "../../../lib/customers";
import JobDrawer from "./JobDrawer";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit" }); } catch { return iso; }
};
const fmtUSD = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const StatusPill = ({ status }) => {
  const c = STATUS_COLORS[status] || { bg: "#E7E5E4", fg: "#1C1917", border: "#A8A29E" };
  return (
    <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>
      {status}
    </span>
  );
};
const PriorityPill = ({ priority, late }) => {
  // When the row is LATE, swap the normal priority chip for a flashing red
  // "LATE" badge — this is the clearest "fix me now" signal on the floor.
  if (late) {
    return (
      <span
        className="late-flash px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm whitespace-nowrap"
        data-testid="late-badge"
        title="Past due — click row to open"
      >
        LATE
      </span>
    );
  }
  const c = PRIORITY_COLORS[priority] || PRIORITY_COLORS.Normal;
  return (
    <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm" style={{ background: c.bg, color: c.fg }}>
      {priority}
    </span>
  );
};

const QueueChip = ({ queue }) => {
  if (!queue) return <span className="text-[12px] text-[color:var(--ups-subtext)]">—</span>;
  const c = QUEUE_COLORS[queue] || { bg: "#E7E5E4", fg: "#1C1917", border: "#A8A29E" };
  return (
    <span className="px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.10em] rounded-sm whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>
      {queue}
    </span>
  );
};

const JobRow = ({ job, members, onClick, onPrint, onPrint4x6, selected, onToggleSelect }) => {
  const owners = (job.owner_ids || []).map((id) => members[id] || "?").join(", ");
  return (
    <tr onClick={onClick} className="cursor-pointer hover:bg-[color:var(--ups-muted)]" data-testid={`job-row-${job.id}`}>
      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onToggleSelect(job.id)}
          className="cursor-pointer"
          data-testid={`job-row-checkbox-${job.id}`}
          aria-label={`Select job ${job.job_number}`}
        />
      </td>
      <td className="px-3 py-2 font-mono text-[11px]">{job.job_number}</td>
      <td className="px-3 py-2"><div className="font-medium text-[13px] truncate max-w-[220px]">{job.name}</div><div className="text-[11px] text-[color:var(--ups-subtext)] truncate max-w-[220px]">{job.customer_name || "—"}</div></td>
      <td className="px-3 py-2"><StatusPill status={job.status} /></td>
      <td className="px-3 py-2"><PriorityPill priority={job.priority} late={isJobLate(job)} /></td>
      <td className="px-3 py-2"><QueueChip queue={job.queue} /></td>
      <td className="px-3 py-2 text-[12px]">{job.customer_type || "—"}</td>
      <td className="px-3 py-2 text-[12px] truncate max-w-[140px]">{owners || "—"}</td>
      <td className="px-3 py-2 text-[12px] text-[color:var(--ups-subtext)] whitespace-nowrap">{fmtDate(job.due_at)}</td>
      <td className="px-3 py-2 font-mono text-[12px]">{fmtUSD(job.total_cost)}</td>
      <td className="px-3 py-2 text-[10px] font-mono text-[color:var(--ups-subtext)]">{job.payment_status}</td>
      <td className="px-2 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrint(job); }}
          className="p-1.5 rounded-sm hover:bg-white"
          style={{ border: "1px solid var(--ups-border)" }}
          aria-label="Print job ticket (Letter)"
          title="Print job ticket — Letter (8.5×11)"
          data-testid={`print-job-${job.id}`}
        >
          <Printer size={13} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrint4x6(job); }}
          className="p-1.5 rounded-sm hover:bg-white ml-1 inline-flex items-center gap-0.5"
          style={{ border: "1px solid var(--ups-border)" }}
          aria-label="Print job ticket (4×6 thermal)"
          title="Print job ticket — 4×6 thermal label"
          data-testid={`print-job-4x6-${job.id}`}
        >
          <Printer size={11} />
          <span className="text-[9px] font-bold leading-none">4×6</span>
        </button>
      </td>
    </tr>
  );
};

const KanbanCard = ({ job, members, onClick, onDragStart }) => {
  const sales = members[job.sales_poc_id] || "";
  const owners = (job.owner_ids || []).map((id) => members[id] || "?").join(", ");
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, job)}
      onClick={onClick}
      className="p-3 mb-2 rounded-sm cursor-pointer card-swiss hover:-translate-y-0.5 transition"
      style={{ background: "#fff" }}
      data-testid={`kanban-card-${job.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="font-mono text-[10px] text-[color:var(--ups-subtext)]">{job.job_number}</div>
        <PriorityPill priority={job.priority} late={isJobLate(job)} />
      </div>
      <div className="font-bold text-[13px] leading-tight mb-1">{job.name}</div>
      <div className="text-[11px] text-[color:var(--ups-subtext)] mb-2">{job.customer_name || "—"} · {job.customer_type || "—"}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono text-[color:var(--ups-subtext)]">
        {job.queue && <span>📦 {job.queue}</span>}
        <span>📅 {fmtDate(job.due_at)}</span>
        <span style={{ color: "var(--ups-accent)", fontWeight: 700 }}>{fmtUSD(job.total_cost)}</span>
      </div>
      {(sales || owners) && (
        <div className="text-[10px] mt-2 pt-2 border-t border-dashed border-[color:var(--ups-border)] text-[color:var(--ups-subtext)]">
          {sales && <span>👤 {sales}</span>}
          {owners && <span className="ml-2">🏷 {owners}</span>}
        </div>
      )}
    </div>
  );
};

const fmtRelative = (d) => {
  if (!d) return "—";
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 5000) return "just now";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  return `${Math.round(m / 60)} hr ago`;
};

/**
 * Live-refresh badge in the Tracker toolbar.
 * Surfaces auto-refresh state + "last sync" timestamp, lets the admin
 * pause polling and force a refresh on demand.
 */
const LiveBadge = ({ on, lastRefreshedAt, paused, onToggle, onRefreshNow }) => {
  // Re-render every 15s so the relative timestamp stays fresh without
  // pinging the API.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const liveColor = !on ? "#A8A29E" : paused ? "#F59E0B" : "#16A34A";
  const liveLabel = !on ? "Off" : paused ? "Paused" : "Live";
  const tooltip = !on
    ? "Auto-refresh is OFF. Click to turn on."
    : paused
      ? "Auto-refresh is paused because a job drawer / new-job form is open."
      : "Auto-refresh is ON. Pulls latest jobs every 30s.";

  return (
    <div
      className="inline-flex items-center rounded-sm overflow-hidden"
      style={{ border: "1px solid var(--ups-border)" }}
      data-testid="tracker-live-badge"
    >
      <button
        type="button"
        onClick={onToggle}
        title={tooltip}
        className="px-2.5 py-2 text-[10px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5"
        data-testid="tracker-live-toggle"
      >
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{ background: liveColor, boxShadow: on && !paused ? `0 0 0 3px ${liveColor}33` : "none" }}
          aria-hidden
        />
        <span style={{ color: liveColor }}>{liveLabel}</span>
        <span className="text-[color:var(--ups-subtext)] font-mono normal-case tracking-normal">
          · {fmtRelative(lastRefreshedAt)}
        </span>
      </button>
      <button
        type="button"
        onClick={onRefreshNow}
        title="Refresh now"
        className="px-2 py-2 hover:bg-[color:var(--ups-muted)]"
        style={{ borderLeft: "1px solid var(--ups-border)" }}
        data-testid="tracker-refresh-now"
      >
        <RefreshCw size={11} />
      </button>
    </div>
  );
};

const NewJobForm = ({ meta, onSave, onCancel, busy }) => {
  const [form, setForm] = useState({ name: "", customer_name: "", customer_type: "", priority: "Normal", queue: "", status: "New Lead", total_cost: 0, description: "" });
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
      className="p-4 mb-4 rounded-sm grid gap-3 sm:grid-cols-3"
      style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }}
      data-testid="new-job-form"
    >
      <input className="input-swiss px-3 py-2 rounded-sm text-sm sm:col-span-2" required placeholder="Job name (e.g. 500 Acme business cards)" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} data-testid="new-job-name" />
      <select className="input-swiss px-3 py-2 rounded-sm text-sm" value={form.customer_type} onChange={(e) => setForm((p) => ({ ...p, customer_type: e.target.value }))}>
        <option value="">Customer Type —</option>
        {meta.customer_types.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <input className="input-swiss px-3 py-2 rounded-sm text-sm" placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm((p) => ({ ...p, customer_name: e.target.value }))} />
      <select className="input-swiss px-3 py-2 rounded-sm text-sm" value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}>
        {meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select className="input-swiss px-3 py-2 rounded-sm text-sm" value={form.queue} onChange={(e) => setForm((p) => ({ ...p, queue: e.target.value }))}>
        <option value="">Queue —</option>
        {meta.queues.map((q) => <option key={q} value={q}>{q}</option>)}
      </select>
      <input type="number" min={0} step="0.01" className="input-swiss px-3 py-2 rounded-sm text-sm font-mono" placeholder="Total cost" value={form.total_cost} onChange={(e) => setForm((p) => ({ ...p, total_cost: parseFloat(e.target.value) || 0 }))} />
      <select className="input-swiss px-3 py-2 rounded-sm text-sm" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
        {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <textarea className="input-swiss px-3 py-2 rounded-sm text-sm sm:col-span-3" rows={2} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
      <div className="sm:col-span-3 flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-3 py-2 text-[12px] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>Cancel</button>
        <button type="submit" disabled={busy} className="btn-brutal px-3 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em]" data-testid="new-job-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Create Job"}
        </button>
      </div>
    </form>
  );
};

const TrackerTab = () => {
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table"); // table | kanban-status | kanban-queue
  const [showArchived, setShowArchived] = useState(false);
  const [filters, setFilters] = useState({ status: "", priority: "", queue: "", customer_type: "", product: "", q: "", late: false });
  // Search input is committed only on Enter / blur (per user request — was firing on every keystroke).
  const [searchDraft, setSearchDraft] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [archiveStats, setArchiveStats] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyCreate, setBusyCreate] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [statusEmailModal, setStatusEmailModal] = useState(null); // { group: {key, name, email, jobs[]} }
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [autoRefreshOn, setAutoRefreshOn] = useState(true);
  const reloadRef = useRef(null);

  const reload = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { archived: showArchived };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (dateFrom) params.from_date = dateFrom;
      if (dateTo) params.to_date = dateTo;
      const [jobsRes, metaRes] = await Promise.all([trackerListJobs(params), meta ? Promise.resolve(meta) : trackerGetMeta()]);
      setJobs(jobsRes);
      if (!meta) setMeta(metaRes);
      setLastRefreshedAt(new Date());
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to load jobs");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  // Keep an up-to-date reference so the polling interval calls the latest closure.
  reloadRef.current = reload;
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [showArchived, filters.status, filters.priority, filters.queue, filters.customer_type, filters.product, filters.q, filters.late, dateFrom, dateTo]);

  // Refresh archived-only stat strip whenever we flip into archived mode.
  useEffect(() => {
    if (!showArchived) { setArchiveStats(null); return; }
    (async () => {
      try { setArchiveStats(await trackerArchiveStats()); }
      catch { /* silent */ }
    })();
  }, [showArchived, jobs.length]);

  // Clear selection whenever the visible jobs change so we don't leak ids across views.
  useEffect(() => { setSelectedIds(new Set()); }, [showArchived, view, filters.q]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMerge = async ({ keeper_id, source_ids }) => {
    try {
      const res = await trackerMergeJobs(keeper_id, source_ids);
      toast.success(`Merged ${res.archived_count} job${res.archived_count === 1 ? "" : "s"} into Job #${res.keeper_job_number}`);
      setSelectedIds(new Set());
      setMergeOpen(false);
      await reload({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    }
  };

  const datePresets = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const fmt = (d) => d.toISOString().slice(0, 10);
    return [
      { label: "This month", from: fmt(new Date(y, m, 1)), to: fmt(today) },
      { label: "Last month", from: fmt(new Date(y, m - 1, 1)), to: fmt(new Date(y, m, 0)) },
      { label: "This year", from: fmt(new Date(y, 0, 1)), to: fmt(today) },
      { label: "All time", from: "", to: "" },
    ];
  };

  // ---- Auto-refresh: every 30s, silently re-pull the jobs list so multi-user
  // edits from other browsers show up without a manual refresh. Pauses when:
  //   • the user opens the job drawer (we don't want to clobber their edits)
  //   • the new-job form is open
  //   • the tab is hidden (no point polling if no one's looking)
  // The toolbar "Live" badge surfaces the current state.
  useEffect(() => {
    if (!autoRefreshOn) return;
    const tick = () => {
      if (document.hidden) return;
      if (selectedJobId) return;
      if (creating) return;
      reloadRef.current?.({ silent: true });
    };
    const id = setInterval(tick, 30000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [autoRefreshOn, selectedJobId, creating]);

  const members = useMemo(() => {
    const m = {};
    (meta?.team_members || []).forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [meta]);

  const handleCreate = async (payload) => {
    setBusyCreate(true);
    try {
      const j = await trackerCreateJob(payload);
      toast.success(`Job ${j.job_number} created`);
      setCreating(false);
      await reload();
      setSelectedJobId(j.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusyCreate(false); }
  };

  const handleDragStart = (e, job) => {
    e.dataTransfer.setData("text/plain", job.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDropTo = (newField, newValue) => async (e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const job = jobs.find((j) => j.id === id);
    if (!job || job[newField] === newValue) return;
    try {
      const updated = await trackerUpdateJob(id, { [newField]: newValue });
      toast.success(updated.archived ? `Moved to ${newValue} (archived)` : `Moved to ${newValue}`);
      await reload();
      // If the drag moved the job INTO "Ready for Pickup", offer to send the
      // branded pickup-ready email to the customer (matches JobDrawer flow).
      if (
        newField === "status" &&
        newValue === "Ready for Pickup" &&
        job.status !== "Ready for Pickup" &&
        (updated.customer_email || "").trim()
      ) {
        const confirmed = window.confirm(
          `📧 Status set to "Ready for Pickup".\n\n` +
          `Send the branded "Order Ready for Pickup" email to ${updated.customer_email}?\n\n` +
          `Includes store address + Google Review CTA.`
        );
        if (confirmed) {
          try {
            const res = await trackerNotifyCustomer(id, {});
            if (res?.status === "sent" || res?.ok) {
              toast.success(`Pickup-ready email sent to ${updated.customer_email}`);
            } else {
              toast.warning(res?.status || "Email send returned no status");
            }
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Pickup email send failed");
          }
        }
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Move failed"); }
  };

  const handleExportCsv = async () => {
    try {
      const params = { archived: showArchived };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const blob = await trackerDownloadCsv(params);
      const url = URL.createObjectURL(new Blob([blob], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `print-jobs-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const handlePrintJob = (job) => {
    window.open(`/admin/jobs/${job.id}/ticket`, "_blank", "noopener,noreferrer");
  };

  const handlePrintJob4x6 = (job) => {
    window.open(`/admin/jobs/${job.id}/ticket?format=4x6`, "_blank", "noopener,noreferrer");
  };

  // QR-code deep-link from a printed Job Ticket: open this job's drawer.
  useEffect(() => {
    const handler = (e) => {
      const jid = e?.detail?.jobId;
      if (jid) setSelectedJobId(jid);
    };
    window.addEventListener("tracker:open-job", handler);
    return () => window.removeEventListener("tracker:open-job", handler);
  }, []);

  if (loading || !meta) return <div className="p-10 text-[color:var(--ups-subtext)] flex items-center"><Loader2 className="animate-spin mr-2" size={16} />Loading tracker…</div>;

  // Group for Kanban
  const groupKey = view === "kanban-queue" ? "queue" : "status";
  const groupValues = view === "kanban-queue" ? meta.queues : meta.statuses;
  const grouped = {};
  groupValues.forEach((g) => { grouped[g] = []; });
  jobs.forEach((j) => {
    const k = j[groupKey] || (groupKey === "queue" ? "Unassigned" : "Unknown");
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(j);
  });

  // Group jobs by customer for the "by-customer" view.
  // Sort within group: newest first (by received_at desc).
  // Sort groups: highest active $ first, then alphabetical.
  //
  // Bug fix: a single customer can have some jobs with `customer_id` set and
  // others with only `customer_email`. We must merge these into ONE bucket per
  // canonical key (id when available, else lowercased email) — otherwise the
  // same customer appears split across multiple groups. Also: orphan jobs
  // (no id, no email) stay in a single "Unassigned" bucket that never inherits
  // a customer name from any random member job.
  const customerGroups = (() => {
    if (view !== "by-customer") return [];
    // ---- Pass 1: bucket by id OR by email (whichever is present first) ----
    const buckets = new Map();
    const emailToKey = new Map();   // lowercased-email -> canonical bucket key
    const ORPHAN_KEY = "__unassigned__";

    const newBucket = (key, j, isOrphan = false) => ({
      key,
      isOrphan,
      name: isOrphan ? "(Unassigned)" : (j.customer_name || j.customer_email || "(no customer)"),
      email: isOrphan ? "" : (j.customer_email || ""),
      type: isOrphan ? "—" : (j.customer_type || "—"),
      total: 0,
      jobs: [],
      _nameLocked: isOrphan, // orphan label is immutable
    });

    for (const j of jobs) {
      const id = j.customer_id || null;
      const emailLc = j.customer_email ? j.customer_email.toLowerCase().trim() : "";

      let key;
      if (id) {
        key = id;
      } else if (emailLc) {
        // Reuse an existing bucket keyed by this email (id-keyed bucket
        // earlier may have already claimed this email — see Pass 2).
        key = emailToKey.get(emailLc) || `email:${emailLc}`;
      } else {
        key = ORPHAN_KEY;
      }

      let row = buckets.get(key);
      if (!row) {
        row = newBucket(key, j, key === ORPHAN_KEY);
        buckets.set(key, row);
      }
      if (emailLc && !emailToKey.has(emailLc)) emailToKey.set(emailLc, key);

      row.jobs.push(j);
      row.total += Number(j.total_cost || 0);
      // Lock name to the first non-empty customer_name we see (orphans excluded)
      if (!row._nameLocked && !row.isOrphan && j.customer_name) {
        row.name = j.customer_name;
        row._nameLocked = true;
      }
    }

    // ---- Pass 2: merge email-keyed buckets into id-keyed buckets when both
    // exist for the same email (customer linked on some jobs, not on others).
    for (const [email, firstKey] of emailToKey.entries()) {
      // Find any id-keyed bucket whose email matches; prefer that as canonical.
      let canonical = null;
      for (const [k, row] of buckets.entries()) {
        if (row.email && row.email.toLowerCase().trim() === email && !k.startsWith("email:")) {
          canonical = k;
          break;
        }
      }
      if (canonical && canonical !== firstKey && buckets.has(firstKey)) {
        const target = buckets.get(canonical);
        const source = buckets.get(firstKey);
        target.jobs.push(...source.jobs);
        target.total += source.total;
        if (!target._nameLocked && source._nameLocked) {
          target.name = source.name;
          target._nameLocked = true;
        }
        buckets.delete(firstKey);
      }
    }

    // ---- Sort jobs within each group, then sort groups ----
    const arr = Array.from(buckets.values());
    arr.forEach((g) => g.jobs.sort((a, b) => {
      const ra = a.received_at || "";
      const rb = b.received_at || "";
      if (ra !== rb) return ra < rb ? 1 : -1;
      return (a.due_at || "").localeCompare(b.due_at || "");
    }));
    arr.sort((a, b) => {
      // Orphan bucket always last
      if (a.isOrphan && !b.isOrphan) return 1;
      if (b.isOrphan && !a.isOrphan) return -1;
      return (b.total - a.total) || a.name.localeCompare(b.name);
    });
    return arr;
  })();

  return (
    <div className="space-y-4" data-testid="tracker-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl font-bold flex items-center gap-2"><Box size={20} /> Production Tracker</h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">{jobs.length} {showArchived ? "archived" : "open"} jobs · drag cards in Kanban view to change status / queue.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <LiveBadge
            on={autoRefreshOn}
            lastRefreshedAt={lastRefreshedAt}
            paused={!!selectedJobId || creating}
            onToggle={() => setAutoRefreshOn((v) => !v)}
            onRefreshNow={() => reload({ silent: true })}
          />
          <button onClick={() => setShowArchived(!showArchived)} className={`px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 ${showArchived ? "bg-[color:var(--ups-ink)] text-white" : ""}`} style={{ border: "1px solid var(--ups-border)" }} data-testid="toggle-archived">
            <Archive size={12} /> {showArchived ? "Open jobs" : "Archived"}
          </button>
          <button onClick={handleExportCsv} className="px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }} data-testid="export-csv">
            <Download size={12} /> CSV
          </button>
          <button onClick={() => setCreating(true)} className="btn-brutal px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" data-testid="new-job-btn">
            <Plus size={12} /> New Job
          </button>
        </div>
      </div>

      {creating && <NewJobForm meta={meta} onSave={handleCreate} onCancel={() => setCreating(false)} busy={busyCreate} />}

      {/* Archived-mode stat strip + date range filter */}
      {showArchived && (
        <div className="space-y-2" data-testid="archived-controls">
          {archiveStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="archived-stats">
              {[
                { label: archiveStats.month_label, value: archiveStats.this_month, key: "this-month", testid: "stat-this-month" },
                { label: "Last month", value: archiveStats.last_month, key: "last-month", testid: "stat-last-month" },
                { label: "This year", value: archiveStats.this_year, key: "this-year", testid: "stat-this-year" },
                { label: "All time", value: archiveStats.total, key: "all-time", testid: "stat-all-time" },
              ].map((s) => (
                <div key={s.key} className="p-3 rounded-sm bg-white" style={{ border: "1px solid var(--ups-border)" }} data-testid={s.testid}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">{s.label}</div>
                  <div className="font-display text-2xl font-black mt-0.5">{s.value}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap p-3 rounded-sm" style={{ background: "#FFF8E1", border: "1px solid #FFB500" }}>
            <Calendar size={14} />
            <span className="text-[11px] font-bold uppercase tracking-[0.14em]">Received between</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
              data-testid="filter-date-from"
            />
            <span className="text-[11px]">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
              data-testid="filter-date-to"
            />
            <div className="flex items-center gap-1 ml-2">
              {datePresets().map((p) => (
                <button
                  key={p.label}
                  onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                  className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm hover:bg-white"
                  style={{ border: "1px solid var(--ups-border)" }}
                  data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                className="ml-auto text-[10px] font-bold uppercase tracking-[0.12em] flex items-center gap-1 hover:opacity-70"
                data-testid="clear-date-range"
              >
                <X size={11} /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Selection / merge bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 p-3 rounded-sm"
          style={{ background: "var(--ups-ink)", color: "#FFB500" }}
          data-testid="merge-bar"
        >
          <div className="text-[12px] font-bold uppercase tracking-[0.14em]">
            {selectedIds.size} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:opacity-80"
              style={{ border: "1px solid #FFB500" }}
              data-testid="merge-bar-clear"
            >
              Clear
            </button>
            <button
              onClick={() => setMergeOpen(true)}
              disabled={selectedIds.size < 2}
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-40"
              style={{ background: "#FFB500", color: "var(--ups-ink)" }}
              data-testid="merge-selected-btn"
              title={selectedIds.size < 2 ? "Select at least 2 jobs to merge" : "Merge selected jobs"}
            >
              <GitMerge size={12} /> Merge {selectedIds.size} jobs
            </button>
          </div>
        </div>
      )}

      {/* Filters + view toggle */}
      <div className="flex items-center gap-2 flex-wrap p-3 rounded-sm" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
        <Filter size={14} className="text-[color:var(--ups-subtext)]" />
        <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))} className="input-swiss px-2 py-1.5 rounded-sm text-[12px]" data-testid="filter-status">
          <option value="">All statuses</option>
          {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters((p) => ({ ...p, priority: e.target.value }))} className="input-swiss px-2 py-1.5 rounded-sm text-[12px]">
          <option value="">All priorities</option>
          {meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.queue} onChange={(e) => setFilters((p) => ({ ...p, queue: e.target.value }))} className="input-swiss px-2 py-1.5 rounded-sm text-[12px]">
          <option value="">All queues</option>
          {meta.queues.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
        <select value={filters.customer_type} onChange={(e) => setFilters((p) => ({ ...p, customer_type: e.target.value }))} className="input-swiss px-2 py-1.5 rounded-sm text-[12px]">
          <option value="">All customer types</option>
          {meta.customer_types.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setFilters((p) => ({ ...p, late: !p.late }))}
          className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1"
          style={filters.late
            ? { background: "#DC2626", color: "#fff", border: "1px solid #991B1B" }
            : { background: "#fff", color: "#DC2626", border: "1px solid #DC2626" }}
          title="Show only jobs past their due date"
          data-testid="filter-late"
        >
          {filters.late ? "✓ LATE only" : "LATE"}
        </button>
        <select
          value={filters.product}
          onChange={(e) => setFilters((p) => ({ ...p, product: e.target.value }))}
          className="input-swiss px-2 py-1.5 rounded-sm text-[12px] font-bold"
          data-testid="filter-product"
          style={filters.product ? {
            background: (PRODUCT_TAG_COLORS[filters.product] || {}).bg,
            color: (PRODUCT_TAG_COLORS[filters.product] || {}).fg,
            borderColor: "var(--ups-ink)",
          } : {}}
        >
          <option value="">All products</option>
          {(meta.product_tags || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]" />
          <input
            type="search"
            placeholder="Search name, customer, order # — press Enter"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setFilters((p) => ({ ...p, q: searchDraft.trim() }));
              } else if (e.key === "Escape") {
                setSearchDraft("");
                setFilters((p) => ({ ...p, q: "" }));
              }
            }}
            onBlur={() => {
              // Commit on blur too so a quick tab-out doesn't lose intent.
              if (searchDraft.trim() !== filters.q) {
                setFilters((p) => ({ ...p, q: searchDraft.trim() }));
              }
            }}
            className="input-swiss w-full pl-7 pr-16 py-1.5 rounded-sm text-[12px]"
            data-testid="filter-search"
          />
          {filters.q && (
            <button
              type="button"
              onClick={() => { setSearchDraft(""); setFilters((p) => ({ ...p, q: "" })); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-[0.12em] flex items-center gap-1 hover:opacity-70"
              data-testid="filter-search-clear"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
        <div className="ml-auto flex items-center rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-ink)" }}>
          {[
            { id: "table", label: "Table", icon: Table2 },
            { id: "by-customer", label: "By Customer", icon: Users },
            { id: "kanban-status", label: "Kanban · Status", icon: LayoutGrid },
            { id: "kanban-queue", label: "Kanban · Queue", icon: LayoutGrid },
          ].map((v) => {
            const Icon = v.icon;
            const active = view === v.id;
            return (
              <button key={v.id} onClick={() => setView(v.id)}
                className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] flex items-center gap-1"
                style={{ background: active ? "var(--ups-ink)" : "#fff", color: active ? "#FFB500" : "var(--ups-ink)" }}
                data-testid={`view-${v.id}`}>
                <Icon size={11} /> {v.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      {jobs.length === 0 ? (
        <div className="p-10 text-center text-[color:var(--ups-subtext)] rounded-sm" style={{ border: "1px dashed var(--ups-border)", background: "#fff" }} data-testid="tracker-empty">
          {showArchived ? "No archived jobs." : "No open jobs match these filters. Create one with the New Job button."}
        </div>
      ) : view === "by-customer" ? (
        <div className="space-y-4" data-testid="by-customer-view">
          <div className="text-[11px] text-[color:var(--ups-subtext)] flex items-center gap-2">
            <Users size={12} /> {customerGroups.length} customer{customerGroups.length !== 1 ? "s" : ""} · click any header to focus the customer's jobs.
          </div>
          {customerGroups.map((group) => {
            const typeColor = CUSTOMER_TYPE_COLORS[group.type] || { bg: "#E7E5E4", fg: "#1C1917", border: "#A8A29E" };
            return (
              <div key={group.key} className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }} data-testid={`customer-group-${group.key}`}>
                {/* Customer header — Materials-style colored band */}
                <div className="flex items-center justify-between px-3 py-2"
                  style={{ background: typeColor.bg, color: typeColor.fg, borderBottom: `2px solid ${typeColor.border}` }}>
                  <div className="flex items-center gap-3">
                    <div className="font-bold text-[14px] uppercase tracking-[0.10em]">{group.name}</div>
                    <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] rounded-sm" style={{ background: "rgba(255,255,255,0.5)", color: typeColor.fg, border: `1px solid ${typeColor.border}` }}>
                      {group.type}
                    </span>
                    {group.email ? <div className="text-[11px] font-mono opacity-75">{group.email}</div> : null}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-mono">
                    <div>{group.jobs.length} {group.jobs.length === 1 ? "job" : "jobs"}</div>
                    <div className="font-bold">{fmtUSD(group.total)}</div>
                    {/* Email button — only when the bucket has a real customer id or email */}
                    {!group.isOrphan && (group.email || !group.key.startsWith("email:")) && (
                      <button type="button"
                        onClick={() => setStatusEmailModal({ group })}
                        disabled={!group.email}
                        title={group.email ? `Email status to ${group.email}` : "No customer email"}
                        className="ml-1 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1 disabled:opacity-40"
                        style={{ background: "rgba(255,255,255,0.7)", color: typeColor.fg, border: `1px solid ${typeColor.border}` }}
                        data-testid={`customer-email-status-${group.key}`}
                      >
                        <Mail size={10} /> Email Status
                      </button>
                    )}
                  </div>
                </div>
                {/* Jobs table for this customer */}
                <table className="w-full text-[12px]">
                  <thead style={{ background: "var(--ups-muted)" }}>
                    <tr className="text-left">
                      {["Job #", "Name", "Status", "Pri", "Queue", "Owners", "Due", "Total", "Payment", ""].map((h, i) => (
                        <th key={`${h}-${i}`} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.jobs.map((j) => {
                      const owners = (j.owner_ids || []).map((id) => members[id] || "?").join(", ");
                      return (
                        <tr key={j.id} onClick={() => setSelectedJobId(j.id)} className="cursor-pointer hover:bg-[color:var(--ups-muted)]" data-testid={`customer-job-row-${j.id}`}>
                          <td className="px-3 py-2 font-mono text-[11px]">{j.job_number}</td>
                          <td className="px-3 py-2"><div className="font-medium text-[13px] truncate max-w-[280px]">{j.name}</div></td>
                          <td className="px-3 py-2"><StatusPill status={j.status} /></td>
                          <td className="px-3 py-2"><PriorityPill priority={j.priority} late={isJobLate(j)} /></td>
                          <td className="px-3 py-2"><QueueChip queue={j.queue} /></td>
                          <td className="px-3 py-2 text-[12px] truncate max-w-[140px]">{owners || "—"}</td>
                          <td className="px-3 py-2 text-[12px] text-[color:var(--ups-subtext)] whitespace-nowrap">{fmtDate(j.due_at)}</td>
                          <td className="px-3 py-2 font-mono text-[12px]">{fmtUSD(j.total_cost)}</td>
                          <td className="px-3 py-2 text-[10px] font-mono text-[color:var(--ups-subtext)]">{j.payment_status}</td>
                          <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={(e) => { e.stopPropagation(); handlePrintJob(j); }}
                              className="p-1.5 rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}
                              aria-label="Print job ticket" title="Print job ticket" data-testid={`customer-print-job-${j.id}`}>
                              <Printer size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      ) : view === "table" ? (
        <div className="rounded-sm overflow-auto" style={{ border: "1px solid var(--ups-border)", background: "#fff" }}>
          <table className="w-full text-[12px]">
            <thead style={{ background: "var(--ups-muted)" }}>
              <tr className="text-left">
              <th className="px-2 py-2 w-8">
                <input
                  type="checkbox"
                  checked={jobs.length > 0 && jobs.every((j) => selectedIds.has(j.id))}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedIds(new Set(jobs.map((j) => j.id)));
                    else setSelectedIds(new Set());
                  }}
                  className="cursor-pointer"
                  aria-label="Select all jobs"
                  data-testid="job-row-checkbox-all"
                />
              </th>
              {["Job #", "Name / Customer", "Status", "Pri", "Queue", "Type", "Owners", "Due", "Total", "Payment Status", ""].map((h, i) => (
                <th key={`${h}-${i}`} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em]">{h}</th>
              ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <JobRow
                  key={j.id}
                  job={j}
                  members={members}
                  onClick={() => setSelectedJobId(j.id)}
                  onPrint={handlePrintJob}
                  onPrint4x6={handlePrintJob4x6}
                  selected={selectedIds.has(j.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 overflow-auto pb-2">
          {Object.entries(grouped).map(([groupName, list]) => {
            const queueColor = view === "kanban-queue" ? (QUEUE_COLORS[groupName] || null) : null;
            return (
              <div
                key={groupName}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDropTo(groupKey, groupName === "Unassigned" || groupName === "Unknown" ? null : groupName)}
                className="rounded-sm p-2 min-h-[200px]"
                style={{
                  background: queueColor ? queueColor.bg : "var(--ups-muted)",
                  border: `1px solid ${queueColor ? queueColor.border : "var(--ups-border)"}`,
                }}
                data-testid={`kanban-col-${groupName}`}
              >
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em]" style={queueColor ? { color: queueColor.fg } : {}}>{groupName}</div>
                  <div className="text-[10px] font-mono text-[color:var(--ups-subtext)]">{list.length}</div>
                </div>
                {list.map((j) => (
                  <KanbanCard key={j.id} job={j} members={members}
                    onClick={() => setSelectedJobId(j.id)}
                    onDragStart={handleDragStart} />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {selectedJobId && (
        <JobDrawer
          jobId={selectedJobId}
          meta={meta}
          onClose={() => setSelectedJobId(null)}
          onSaved={() => reload()}
          onDeleted={() => { setSelectedJobId(null); reload(); }}
        />
      )}
      {statusEmailModal && (
        <CustomerStatusEmailModal
          group={statusEmailModal.group}
          onClose={() => setStatusEmailModal(null)}
        />
      )}
      {mergeOpen && (
        <JobMergeModal
          jobs={jobs.filter((j) => selectedIds.has(j.id))}
          onClose={() => setMergeOpen(false)}
          onConfirm={handleMerge}
        />
      )}
    </div>
  );
};

const CustomerStatusEmailModal = ({ group, onClose }) => {
  const [message, setMessage] = useState(`Hi ${group.name || "there"},`);
  const [sending, setSending] = useState(false);
  const activeJobs = group.jobs.filter((j) => !j.archived);
  const customerId = group.key.startsWith("email:") ? null : group.key;

  const handleSend = async () => {
    if (!customerId) {
      toast.error("This bucket isn't linked to a Customer record — open one job and set a customer first.");
      return;
    }
    if (!group.email) {
      toast.error("No customer email available.");
      return;
    }
    setSending(true);
    try {
      const r = await customerSendStatusEmail(customerId, { message });
      toast.success(`Status email sent to ${r.recipient} (CC ${r.cc?.[0] || "none"}) — ${r.job_count} job(s).`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="customer-status-email-modal">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-[640px] rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2"><Mail size={18} /> Email Status Update</h3>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 text-[12px]">
          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">To</div>
              <div className="font-mono text-[12px]">{group.email || "(no email)"}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">CC</div>
              <div className="font-mono text-[12px]">store3423@theupsstore.com</div>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1">Subject</div>
            <div className="font-bold text-[13px]">Job status update from UPS Store 3423 — {activeJobs.length} jobs</div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)] mb-1 block">
              Personal message (optional, appears at top of email)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="input-swiss w-full p-2 rounded-sm text-[12px] font-mono"
              data-testid="status-email-message"
            />
          </div>

          <div className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)" }}>
            <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ background: "var(--ups-muted)" }}>
              Jobs to include · {activeJobs.length}
            </div>
            <div className="max-h-[240px] overflow-y-auto">
              <table className="w-full text-[12px]">
                <tbody>
                  {activeJobs.map((j) => (
                    <tr key={j.id} className="border-t" style={{ borderColor: "var(--ups-border)" }}>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{j.job_number}</td>
                      <td className="px-3 py-1.5 truncate max-w-[200px]">{j.name}</td>
                      <td className="px-3 py-1.5"><span className="text-[10px] font-bold uppercase">{j.status}</span></td>
                      <td className="px-3 py-1.5 text-right text-[11px] text-[color:var(--ups-subtext)]">{j.due_at ? new Date(j.due_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: "var(--ups-border)" }}>
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]"
              style={{ border: "1px solid var(--ups-border)" }} data-testid="status-email-cancel">
              Cancel
            </button>
            <button type="button" onClick={handleSend} disabled={sending || !group.email || !customerId || activeJobs.length === 0}
              className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 disabled:opacity-40"
              data-testid="status-email-send">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Send to {group.email || "customer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackerTab;

/* ----------------------------------------------------------------------
 * JobMergeModal — picks the keeper (primary) job and confirms the merge.
 * Notes + attachments + customer/order refs from the others move to the
 * keeper; sources are soft-archived with a "Merged into Job #N" tombstone.
 * --------------------------------------------------------------------*/
const JobMergeModal = ({ jobs, onClose, onConfirm }) => {
  const [keeperId, setKeeperId] = useState(jobs[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const sourceIds = jobs.filter((j) => j.id !== keeperId).map((j) => j.id);

  const handleConfirm = async () => {
    if (!keeperId || sourceIds.length === 0) return;
    setBusy(true);
    try {
      await onConfirm({ keeper_id: keeperId, source_ids: sourceIds });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="job-merge-modal">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-[640px] rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2">
            <GitMerge size={18} /> Merge {jobs.length} jobs
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[12px] text-[color:var(--ups-subtext)]">
            Pick the <b>primary</b> job to keep. The others will be soft-archived with a "Merged into Job #N" note — notes, attachments,
            product tags, customer/order links, and totals are moved to the primary.
          </p>
          <div className="space-y-1.5">
            {jobs.map((j) => (
              <label
                key={j.id}
                className={`flex items-center gap-3 p-2.5 rounded-sm cursor-pointer ${keeperId === j.id ? "bg-[#FFF8E1]" : "hover:bg-[color:var(--ups-muted)]"}`}
                style={{ border: `1.5px solid ${keeperId === j.id ? "#FFB500" : "var(--ups-border)"}` }}
                data-testid={`merge-keeper-option-${j.id}`}
              >
                <input
                  type="radio"
                  name="keeper"
                  checked={keeperId === j.id}
                  onChange={() => setKeeperId(j.id)}
                  className="cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold truncate">
                    <span className="font-mono text-[11px] mr-2">{j.job_number}</span>
                    {j.name || "Untitled job"}
                  </div>
                  <div className="text-[11px] text-[color:var(--ups-subtext)] truncate">
                    {j.customer_name || "—"} · {j.status} · {fmtUSD(j.total_cost)} · received {fmtDate(j.received_at)}
                  </div>
                </div>
                {keeperId === j.id && (
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm" style={{ background: "#FFB500", color: "var(--ups-ink)" }}>
                    Primary
                  </span>
                )}
              </label>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]"
              style={{ border: "1px solid var(--ups-border)" }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={busy || sourceIds.length === 0}
              className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 disabled:opacity-40"
              data-testid="merge-confirm-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <GitMerge size={14} />}
              Merge {sourceIds.length} into primary
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
